import type { ReactNode } from 'react';

import classNames from 'classnames';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageProxy from '@next-image-proxy';

import { Button, ExternalLink } from '@uikit';

import type { ItemsProps, ProjectContentTableOfContentTab } from '../types';
import { depositLimits, gallery, sqrActions } from './constants';

export type SubTitleProps = {
  children: ReactNode;
  className?: string;
};

export type ListProps = {
  isOrdered?: boolean;
  items: Array<ItemsProps>;
  className?: string;
};

const SubTitle = ({ children, className }: SubTitleProps) => {
  return <h3 className={classNames('text-2xl font-semibold', className)}>{children}</h3>;
};

const List = ({ isOrdered, items, className }: ListProps) => {
  const ListTag = isOrdered ? 'ol' : 'ul';

  return (
    <ListTag className={classNames(isOrdered ? 'list-decimal' : 'list-disc', className)}>
      {items.map(({ id, content, item }) => {
        if (Array.isArray(item)) {
          return (
            <li key={id} className="font-normal">
              {content}
              <ul className="list-disc">
                {item.map(({ id: subId, subItem }) => (
                  <li key={subId}>{subItem}</li>
                ))}
              </ul>
            </li>
          );
        } else {
          return <li key={id}>{item}</li>;
        }
      })}
    </ListTag>
  );
};

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'intro',
    title: 'Introduction',
    content: (
      <>
        <p className="mb-4 max-sm:text-sm font-normal">
          Magic Square and FoxsyAI, the AI-driven robotics and blockchain project revolutionizing
          autonomous soccer, are teaming up for a massive $FOXSY token giveaway! This campaign
          includes:
        </p>
        <p className="mb-4 max-sm:text-sm font-normal">
          ✅ <span className="font-bold">Magic Drop – 300,000 $FOXSY</span> for SQRp holders (March
          25, 2025)
        </p>
        <p className="mb-6 max-sm:text-sm font-normal">
          ✅ <span className="font-bold">Trading Competition – 500,000 $FOXSY</span> prize pool
          (April 1, 2025)
        </p>
        <div className="overflow-hidden">
          <Swiper
            modules={[Pagination]}
            pagination={{
              enabled: true,
              clickable: true,
            }}
            slidesPerView="auto"
            className="pb-12"
          >
            {gallery.map((galleryItem, index) => (
              <SwiperSlide key={index} className="w-4/5 px-1">
                <ImageProxy
                  alt={`foxsyai-${index + 1}`}
                  width={640}
                  height={292}
                  quality={100}
                  className="w-full h-auto select-none"
                  src={galleryItem.image}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </>
    ),
  },
  {
    id: 'about',
    title: 'What is FoxsyAI?',
    content: (
      <>
        <p className="mb-4 max-sm:text-sm font-normal">
          FoxsyAI is a cutting-edge AI, robotics, and blockchain project building the future of
          autonomous soccer robots. A key contributor to RoboCup, FoxsyAI leverages AI training,
          simulation tournaments, and blockchain incentives to push the boundaries of robotic sports
          and AI research.
        </p>
        <p className="font-normal">
          💡 <span className="font-bold">FoxsyAI’s Vision:</span> By mid-21st century,
          <span className="font-bold"> autonomous humanoid robots</span> will defeat the reigning
          <span className="font-bold"> FIFA World Cup Champions</span> in a soccer match.
        </p>

        <p className="font-normal">
          ⚽ <span className="font-bold">FoxsyAI Tournaments –</span> Train AI-powered soccer teams
          to compete in simulation-based tournaments.
        </p>

        <p className="font-normal">
          🛍️ <span className="font-bold">FoxsyAI Store –</span> Customize players, fields, and
          strategies while fueling a creator-driven economy.
        </p>

        <p className="font-normal">
          🔗 <span className="font-bold">Blockchain & AI Integration –</span> Earn, stake, and
          participate using the
          <span className="font-bold"> $FOXSY token</span>.
        </p>

        <p className="font-normal mb-4">
          👉 <span className="font-bold">Learn more about FoxsyAI and $FOXSY: </span>
          <span className="font-bold">
            <ExternalLink href="https://foxsy.ai/" className="text-base-text-blue underline">
              Visit FoxsyAI
            </ExternalLink>
          </span>
        </p>

        <SubTitle className="mb-6 max-sm:text-sm font-normal">
          Stay Connected & Be Part of the Future!
        </SubTitle>
        <p className="mb-6 max-sm:text-sm font-normal">
          Join the <span className="font-bold">FoxsyAI community</span> to stay ahead of the latest
          developments in{' '}
          <span className="font-bold">
            AI-driven sports, robotics innovation, and blockchain-powered gaming.
          </span>
        </p>
        <p className="mb-6 max-sm:text-sm font-normal">
          💬 Follow them on Twitter for real-time updates, major announcements, and
          behind-the-scenes insights.{' '}
          <ExternalLink href="https://x.com/foxsy_ai" className="text-base-text-blue underline">
            Follow Now
          </ExternalLink>
          .
        </p>
        <p className="mb-6 max-sm:text-sm font-normal">
          📢 Join their Telegram to engage with the team, participate in exclusive discussions, and
          be the first to access upcoming tournaments, token rewards, and special events.{' '}
          <ExternalLink href="https://t.me/foxsyai" className="text-base-text-blue underline">
            Join Now
          </ExternalLink>
          .
        </p>
      </>
    ),
  },
  {
    id: 'why-hold-foxsy',
    title: 'Why Hold $FOXSY?',
    content: (
      <>
        <p className="mb-4 max-sm:text-sm font-normal">
          The <span className="font-bold">$FOXSY token</span> powers the FoxsyAI ecosystem, offering
          holders:
        </p>
        <p className="mb-4 max-sm:text-sm font-normal">
          🔹 Entry into AI-powered soccer tournaments 🏆
        </p>
        <p className="mb-4 max-sm:text-sm font-normal">
          🔹 Customization of AI players & team strategies ⚡
        </p>
        <p className="mb-4 max-sm:text-sm font-normal">
          🔹 Earning potential through staking & metastaking 🔥
        </p>
        <p className="mb-4 max-sm:text-sm font-normal">
          🔹 Governance rights to influence FoxsyAI’s development 🗳️
        </p>
        <p className=" max-sm:text-sm font-normal">
          🔹 Exclusive rewards for liquidity providers 💰
        </p>
      </>
    ),
  },
  {
    id: 'campaign-details',
    title: 'Campaign Details',
    content: (
      <>
        <SubTitle className="mb-6 max-sm:text-sm font-normal">
          Phase 1: FOXSY Magic Drop – 300,000 $FOXSY!
        </SubTitle>
        <p className="mb-6 max-sm:text-sm font-bold">
          💎 Exclusive for Magic Square SQRp Holders 💎
          <br />
          🔹 How It Works:
        </p>
        <p className="mb-6 max-sm:text-sm font-normal">
          Users can deposit <span className="font-bold">SQRp Points</span> to claim a{' '}
          <span className="font-bold">pro-rata share of 300,000 $FOXSY</span>. The more{' '}
          <span className="font-bold">SQRp</span> deposited, the{' '}
          <span className="font-bold">larger your $FOXSY allocation!</span>
        </p>
        <p className="mb-4 max-sm:text-sm font-bold">
          🔹 Deposit Limits (Based on Magic Store Membership Tier):
        </p>
        <List items={depositLimits} className="mb-6 max-sm:text-sm font-normal list-none" />
        <p className="mb-6 max-sm:text-sm font-normal">
          📅 <span className="font-bold">Claiming Begins:</span> April 1, 2025, with a{' '}
          <span className="font-bold">30-day linear vesting period.</span>
        </p>
        <p className="mb-6 max-sm:text-sm font-normal">
          🔥 <span className="font-bold">Need SQRp Points?</span> Earn them by staking{' '}
          <span className="font-bold">$SQR:</span>
        </p>
        <List items={sqrActions} className="mb-4 max-sm:text-sm font-normal list-none" />
        <SubTitle className="mb-6 max-sm:text-sm font-normal">
          Phase 2: FOXSY Trading Competition – 500,000 $FOXSY!
        </SubTitle>
        <p className="mb-6 max-sm:text-sm font-bold">
          🚀 Starts April 1, 2025 – Compete in a high-stakes trading battle to win a share of
          500,000 $FOXSY!
        </p>
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 text-[16px] px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink href="https://magicsquare.io/store/stories/foxsy-ai-1000000-trading-competition">
            🔗 Join the Trading Competition
          </ExternalLink>
        </Button>
      </>
    ),
  },
  {
    id: 'how-to-participate',
    title: 'How to Participate',
    content: (
      <>
        <p className="mb-4 max-sm:text-sm font-normal">
          ✅ <span className="font-bold">Step 1:</span> Open a{' '}
          <span className="font-bold">Magic Launchpad account</span>
        </p>
        <p className="mb-4 max-sm:text-sm font-normal">
          ✅ <span className="font-bold">Step 2:</span> Connect a{' '}
          <span className="font-bold">Solana wallet</span> to your{' '}
          <span className="font-bold">MagicID</span>
        </p>
        <p className="mb-4 max-sm:text-sm font-normal">
          ✅ <span className="font-bold">Step 3:</span> Deposit{' '}
          <span className="font-bold">SQRp Points</span> starting March 25, 2025
        </p>
        <p className="mb-4 max-sm:text-sm font-normal">
          ✅ <span className="font-bold">Step 4:</span> Claim your{' '}
          <span className="font-bold">$FOXSY rewards</span> on April 1, 2025
        </p>
        <p className="mb-6 max-sm:text-sm font-normal">
          ✅ <span className="font-bold">Step 5:</span> Join the{' '}
          <span className="font-bold">Trading Competition</span> for additional rewards!
        </p>
        <SubTitle className="max-sm:text-sm font-normal">
          💡 Be part of the AI-powered soccer revolution – claim and trade $FOXSY today!
        </SubTitle>
      </>
    ),
  },
];
